import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactModal from 'react-modal';
import axios from 'axios';
import dayjs from 'dayjs';
import { Alert, FieldGroup, Collapsible, Loading } from '../../components';
import { Button, ButtonGroup } from '../../components/ui';
import { Window } from '../../contexts';
import { STEPS } from '../constants';
import { useOrderManagement } from '../useOrderManagement';
import { SectionTrigger } from './SectionTrigger';

import './tracking-number.scss';
import { currencyMeta, getBaseCurrency } from '../../core/currency';
import { requireResponse } from '../../core/errors/requireResponse';
import { usePoll } from '../../hooks/usePoll';

// A shipment in one of these states blocks the create UI (duplicate
// prevention) — the card renders instead.
const ACTIVE_STATUSES = ['creating', 'created', 'unknown_outcome', 'manual'];

// Sentinel option id for the legacy "type a tracking number" path.
const MANUAL_OPTION = '__manual__';

// color = the app's standard .order-status pill palette (green/orange/red/grey).
const STATUS_CHIPS = {
  creating: { label: 'Creating…', color: 'grey' },
  created: { label: 'Shipment created', color: 'green' },
  manual: { label: 'Tracking saved', color: 'green' },
  unknown_outcome: { label: 'Unknown outcome', color: 'orange' },
  error: { label: 'Failed', color: 'red' },
  cancelled: { label: 'Cancelled', color: 'grey' },
};


const modalStyle = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    borderRadius: '5px',
    border: 'none',
  },
};

// Near-fullscreen viewer for the shipping label (PDF or image).
const labelModalStyle = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    borderRadius: '5px',
    border: 'none',
    padding: '0',
    width: '80vw',
    height: '85vh',
    display: 'flex',
    flexDirection: 'column',
  },
};

// "just now" / "14m ago" / "3h ago" / "2d ago". dayjs' relativeTime plugin is
// not registered app-wide (see core/utils/time.js, which diffs by hand), so
// this stays local rather than pulling a plugin in for one line.
const timeAgo = (value) => {
  if (!value) return null;
  const then = dayjs(value);
  if (!then.isValid()) return null;
  const minutes = dayjs().diff(then, 'minute');
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = dayjs().diff(then, 'hour');
  if (hours < 24) return `${hours}h ago`;
  return `${dayjs().diff(then, 'day')}d ago`;
};

const formatMoment = (value) => {
  const at = dayjs(value);
  return at.isValid() ? at.format('D MMM YYYY, HH:mm') : null;
};

function EnvBadge({ environment }) {
  if (environment === 'production') {
    return (
      <span className="shipment-env shipment-env--prod">
        PRODUCTION — live, billable
      </span>
    );
  }
  return (
    <span className="shipment-env shipment-env--uat">
      Sandbox (UAT) — no real charges
    </span>
  );
}

// The address itself, without the surrounding block — shared by the read-only
// summary and the editable review block's display mode.
function AddressLines({ addr }) {
  if (!addr) return null;
  const cityLine = [addr.city, addr.state, addr.postcode].filter(Boolean).join(', ');
  return (
    <div className="shipment-address__lines">
      {addr.name && <div>{addr.name}</div>}
      {addr.address1 && <div>{addr.address1}</div>}
      {addr.address2 && <div>{addr.address2}</div>}
      {cityLine && <div>{cityLine}</div>}
      {addr.country && <div>{addr.country}</div>}
      {(addr.phone || addr.email) && (
        <div className="shipment-address__contact">
          {[addr.phone, addr.email].filter(Boolean).join(' · ')}
        </div>
      )}
    </div>
  );
}

// Read-only address summary (ship_to / ship_from from .../shipping-info).
// No explicit text colours — inherits the light or dark surface it sits on.
function AddressBlock({ title, addr }) {
  if (!addr) return null;
  return (
    <div className="shipment-address">
      <div className="shipment-address__title">{title}</div>
      <AddressLines addr={addr} />
    </div>
  );
}

// Fields a rep may correct in the confirm step. Mirrors
// ShippingService::OVERRIDABLE_ADDRESS_FIELDS — the server re-validates every
// edit and silently drops anything not on its own list.
//
// No "required" markers here on purpose: which fields are mandatory differs
// between ship-to and ship-from (and the server owns that rule), so the
// authoritative missing[] list does the flagging instead of a marker that
// could disagree with it.
const ADDRESS_FIELDS = [
  { key: 'name', label: 'Name' },
  { key: 'address1', label: 'Address line 1' },
  { key: 'address2', label: 'Address line 2' },
  { key: 'city', label: 'City / town' },
  { key: 'state', label: 'Region / state' },
  { key: 'postcode', label: 'Postcode' },
  { key: 'country', label: 'Country', type: 'country' },
  { key: 'phone', label: 'Phone' },
  { key: 'email', label: 'Email' },
];

/**
 * Review-and-edit block for ship-to / ship-from in the confirm modal.
 *
 * Display mode is the same read-only AddressBlock shown elsewhere; Edit
 * reveals the fields. `addr` is ALWAYS the server's merged answer from
 * .../shipping-info — never a locally assembled guess — so what the rep
 * reviews is exactly what the carrier will be sent. Inputs bind to the local
 * pending edit first (immediate feedback while the debounced re-validation is
 * in flight) and fall back to the server value.
 */
function EditableAddressBlock({
  title,
  addr,
  defaults,
  edits,
  onChange,
  onReset,
  disabled,
}) {
  const [editing, setEditing] = useState(false);
  const editedKeys = Object.keys(edits || {});

  // {id: 'GB', name: 'United Kingdom'} — the override field is an alpha-2 code.
  const countryOptions = (Window.COUNTRIES || [])
    .filter((c) => c.alpha2Code)
    .map((c) => ({ id: c.alpha2Code, name: c.name }));

  const valueFor = (key) => {
    const pending = edits?.[key];
    // ?? not || — '' is a deliberately cleared field, not "unset".
    return pending ?? addr?.[key] ?? '';
  };

  return (
    <div className="shipment-address shipment-address--editable">
      <div className="shipment-address__head">
        <div className="shipment-address__title">{title}</div>
        <div className="shipment-address__tools">
          {editedKeys.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              data-btn="tracking-cancel-address"
              disabled={disabled}
              onClick={() => {
                onReset();
                setEditing(false);
              }}
            >
              Reset
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            data-btn="tracking-edit-address"
            disabled={disabled}
            onClick={() => setEditing((v) => !v)}
          >
            {editing ? 'Done' : 'Edit'}
          </Button>
        </div>
      </div>

      {!editing && <AddressLines addr={addr} />}

      {editing && (
        <div className="shipment-address__form">
          {ADDRESS_FIELDS.map((f) => {
            const changed = Object.prototype.hasOwnProperty.call(edits || {}, f.key);
            return (
              <label key={f.key} className={changed ? 'is-changed' : undefined}>
                <span className="shipment-address__field-label">
                  {f.label}
                  {changed && (
                    <em title={`Order/client value: ${defaults?.[f.key] || '—'}`}>
                      edited
                    </em>
                  )}
                </span>
                {f.type === 'country' ? (
                  <select
                    value={valueFor(f.key)}
                    disabled={disabled}
                    onChange={(e) => onChange(f.key, e.target.value)}
                  >
                    <option value="">—</option>
                    {countryOptions.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    autoComplete="off"
                    value={valueFor(f.key)}
                    disabled={disabled}
                    onChange={(e) => onChange(f.key, e.target.value)}
                  />
                )}
              </label>
            );
          })}
        </div>
      )}

      {editedKeys.length > 0 && !editing && (
        <div className="shipment-address__edited-note">
          Edited for this shipment: {editedKeys.join(', ')}
        </div>
      )}
    </div>
  );
}

// The API authenticates via the axios Authorization header, so the label
// must be fetched as a blob (a plain window.open would 401).
const fetchLabelBlob = async (shipmentId) => {
  const response = await axios.get(`/api/shipments/${shipmentId}/label`, {
    responseType: 'blob',
  });
  return response.data;
};

const triggerDownload = (url, filename) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
};

async function downloadLabel(shipment) {
  try {
    const blob = await fetchLabelBlob(shipment.id);
    const url = URL.createObjectURL(blob);
    triggerDownload(url, `label-${shipment.tracking_number || shipment.id}`);
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  } catch (e) {
    // The global axios interceptor surfaces the error toast.
  }
}

function ShipmentCard({ shipment, busy, onRetry, onRefresh, onCheckStatus, onUpdateTracking }) {
  const [copied, setCopied] = useState(false);
  // Label viewer: {url, isImage} while open; the object URL is revoked on close.
  const [labelView, setLabelView] = useState(null);
  const [labelLoading, setLabelLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  // Inline tracking-number edit (manual shipments only — PUT, never a new row).
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState('');

  const chip = STATUS_CHIPS[shipment.status] || { label: shipment.status, color: 'grey' };

  // A 'creating' shipment is a billable call still in flight — auto-poll the
  // order's shipments every 5s so the rep sees the outcome without acting.
  // Stops on any other status (the card re-renders with the new prop) and on
  // unmount. 'created' is a terminal success — never polled.
  // usePoll (M4.1): backs off on errors, stops on 403/404, pauses when hidden.
  const pollStatus = useCallback((poll) => onCheckStatus?.(poll), [onCheckStatus]);
  usePoll(pollStatus, { interval: 5000, enabled: shipment.status === 'creating' && !!onCheckStatus });

  // Revoke the label object URL if the card unmounts while the viewer is open.
  useEffect(() => {
    return () => {
      if (labelView?.url) URL.revokeObjectURL(labelView.url);
    };
  }, [labelView]);

  const checkStatus = async () => {
    if (checking || !onCheckStatus) return;
    setChecking(true);
    try {
      await onCheckStatus();
    } finally {
      setChecking(false);
    }
  };

  const copyTracking = async () => {
    try {
      await navigator.clipboard.writeText(shipment.tracking_number);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // Clipboard API unavailable (http / permissions) — nothing to do.
    }
  };

  const viewLabel = async () => {
    if (labelLoading) return;
    setLabelLoading(true);
    try {
      const blob = await fetchLabelBlob(shipment.id);
      setLabelView({
        url: URL.createObjectURL(blob),
        isImage: (blob.type || '').startsWith('image/'),
      });
    } catch (e) {
      // The global axios interceptor surfaces the error toast.
    } finally {
      setLabelLoading(false);
    }
  };

  const closeLabelView = () => {
    if (labelView?.url) URL.revokeObjectURL(labelView.url);
    setLabelView(null);
  };

  const startEdit = () => {
    setEditValue(shipment.tracking_number || '');
    setEditError('');
    setEditing(true);
  };

  const saveEdit = async () => {
    if (savingEdit) return;
    if (!editValue) {
      setEditError('Enter a tracking number first.');
      return;
    }
    setSavingEdit(true);
    setEditError('');
    try {
      await onUpdateTracking(shipment, editValue);
      setEditing(false);
    } catch (err) {
      setEditError(
        err?.response?.data?.message || 'Failed to update the tracking number.'
      );
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <div className="shipment-card">
      <div className="shipment-card__head">
        <div className="shipment-card__partner">
          {shipment.account_label || shipment.carrier_name || shipment.carrier}
        </div>
        <span className={`order-status ${chip.color} shipment-card__status`}>
          {chip.label}
        </span>
      </div>

      {shipment.status === 'unknown_outcome' && (
        <div className="shipment-card__note shipment-card__note--warn">
          Unknown outcome — check the carrier dashboard before retrying; contact a master admin.
        </div>
      )}

      {shipment.status === 'error' && (
        <div className="shipment-card__note shipment-card__note--error">
          {shipment.error_message || 'The carrier rejected this shipment.'}
        </div>
      )}

      {/* The label was booked against reviewed values, not the order's own —
          keep that visible after the fact, not just in the modal. */}
      {shipment.overridden_fields?.length > 0 && (
        <div className="shipment-card__note shipment-card__note--info">
          Booked with details corrected in the review step:{' '}
          {shipment.overridden_fields.join(', ')}.
          {shipment.ship_to_saved_to_order === true
            ? ' Also saved to the order address.'
            : shipment.ship_to_saved_to_order === false
            ? ' Not saved to the order address.'
            : ''}
        </div>
      )}

      {editing ? (
        <div className="shipment-card__edit">
          <input
            type="text"
            value={editValue}
            placeholder="Tracking number"
            onChange={(e) => setEditValue(e.target.value)}
          />
          <Button
            variant="primary"
            size="sm"
            data-btn="tracking-save-number"
            loading={savingEdit}
            onClick={saveEdit}
          >
            Save
          </Button>
          <Button
            variant="ghost"
            size="sm"
            data-btn="tracking-cancel-number"
            disabled={savingEdit}
            onClick={() => setEditing(false)}
          >
            Cancel
          </Button>
          {editError && <div className="shipment-card__note shipment-card__note--error">{editError}</div>}
        </div>
      ) : (
        shipment.tracking_number && (
          <div className="shipment-card__tracking">
            <span className="shipment-card__number">{shipment.tracking_number}</span>
            <Button variant="ghost" size="sm" data-btn="tracking-duplicate-number" onClick={copyTracking}>
              {copied ? 'Copied' : 'Copy'}
            </Button>
            {shipment.status === 'manual' && (
              <Button variant="ghost" size="sm" data-btn="tracking-edit-number" onClick={startEdit}>
                Edit
              </Button>
            )}
            {shipment.tracking_link && (
              <a
                className="shipment-card__link"
                href={shipment.tracking_link}
                target="_blank"
                rel="noreferrer"
              >
                Track shipment
              </a>
            )}
          </div>
        )
      )}

      {/* Live carrier tracking. `status` above is the ROW lifecycle and never
          moves once the label exists, so the parcel's journey gets its own
          line — fed by the hourly shipping:refresh-tracking cron and the
          Refresh tracking button below. */}
      {shipment.is_api && shipment.status === 'created' && (
        <div className="shipment-card__delivery">
          <div className="shipment-card__delivery-line">
            {shipment.delivered && (
              <span className="order-status green shipment-card__status">Delivered</span>
            )}
            {shipment.carrier_status ? (
              <span className="shipment-card__carrier-status">
                {shipment.carrier_status}
              </span>
            ) : (
              !shipment.delivered && (
                <span className="shipment-card__carrier-status shipment-card__carrier-status--none">
                  No carrier update yet
                </span>
              )
            )}
          </div>
          <div className="shipment-card__delivery-meta">
            {shipment.delivered && formatMoment(shipment.delivered_at)
              ? `Delivered ${formatMoment(shipment.delivered_at)} · `
              : ''}
            {shipment.tracking_checked_at
              ? `Carrier checked ${timeAgo(shipment.tracking_checked_at)}`
              : 'Not checked yet — polled hourly, or use Refresh tracking'}
          </div>
        </div>
      )}

      <div className="shipment-card__actions">
        {shipment.label_available && (
          <>
            <Button
              variant="primary"
              data-btn="tracking-open-label"
              loading={labelLoading}
              onClick={viewLabel}
            >
              View label
            </Button>
            <Button
              variant="secondary"
              data-btn="tracking-save-label"
              onClick={() => downloadLabel(shipment)}
            >
              Download label
            </Button>
          </>
        )}
        {shipment.is_api && shipment.status === 'created' && (
          <Button
            variant="secondary"
            data-btn="tracking-search-carrier"
            loading={busy}
            onClick={() => onRefresh(shipment)}
          >
            Refresh tracking
          </Button>
        )}
        {(shipment.status === 'creating' || shipment.status === 'unknown_outcome') && (
          <Button
            variant="secondary"
            data-btn="tracking-search-status"
            loading={checking}
            onClick={checkStatus}
          >
            Check status
          </Button>
        )}
        {shipment.status === 'error' && (
          <Button
            variant="primary"
            data-btn="tracking-create-retry"
            loading={busy}
            onClick={() => onRetry(shipment)}
          >
            Retry
          </Button>
        )}
      </div>

      <ReactModal
        {...{
          isOpen: !!labelView,
          onRequestClose: closeLabelView,
          portalClassName: 'payment-dialog-portal',
          style: labelModalStyle,
        }}
      >
        <div className="label-viewer">
          <div className="label-viewer__bar">
            <span className="label-viewer__title">
              Label{shipment.tracking_number ? ` — ${shipment.tracking_number}` : ''}
            </span>
            <div className="label-viewer__buttons">
              <Button
                variant="primary"
                data-btn="tracking-save-file"
                onClick={() =>
                  labelView &&
                  triggerDownload(
                    labelView.url,
                    `label-${shipment.tracking_number || shipment.id}`
                  )
                }
              >
                Download
              </Button>
              <Button variant="secondary" data-btn="tracking-cancel-viewer" onClick={closeLabelView}>
                Close
              </Button>
            </div>
          </div>
          <div className="label-viewer__body">
            {labelView?.isImage ? (
              <img src={labelView.url} alt="Shipping label" />
            ) : (
              <iframe src={labelView?.url} title="Shipping label" />
            )}
          </div>
        </div>
      </ReactModal>
    </div>
  );
}

/**
 * Tracking Number step.
 *
 * Flow branches (top to bottom):
 *  1. Shipping accounts + existing shipments are fetched when the step first
 *     becomes reachable. If the accounts fetch fails or returns nothing, the
 *     legacy manual input renders alone — saving a tracking number is never
 *     blocked by the shipping-provider integration.
 *  2. An ACTIVE shipment (creating/created/unknown_outcome/manual) — or, if
 *     none, the latest errored one — renders as a card instead of any create
 *     UI (duplicate prevention). Error cards offer Retry; manual cards offer
 *     inline tracking-number Edit (PUT — never a second row).
 *  3. Otherwise a "Shipping Partner" dropdown picks between the store's
 *     enabled accounts and "Other / enter manually":
 *       - API account   → "Ship to" summary (POST .../shipping-info) +
 *                         service/weight/dimensions form + REVIEW modal +
 *                         POST /api/orders/{id}/shipments
 *
 *                         The review modal is the confirm step AND the edit
 *                         step: ship-to, ship-from and the parcel are all
 *                         correctable in place. Each change re-POSTs
 *                         shipping-info (debounced), so the displayed values,
 *                         the missing[] list and ready are always the SERVER's
 *                         verdict on the exact payload — the frontend never
 *                         decides what counts as complete. Confirm is disabled
 *                         while a re-check is in flight, on any missing field,
 *                         and when the fetch failed (fail-safe). Corrections
 *                         apply to this shipment only unless "Also save this
 *                         address to the order" is ticked.
 *       - manual account → tracking input, POST .../shipments with
 *                          {shipping_account_id, tracking_number}
 *       - other/manual  → the legacy PUT via saveTrackingNumber (unchanged)
 */
export function TrackingNumber({
  step,
  lastStep,
  isPxOrder,
  paidAmount,
  trackingNumber,
  setTrackingNumber,
  saveTrackingNumber,
}) {
  const [showDialog, setShowDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const { isInline, setStep, order } = useOrderManagement();

  // Shipping-provider state (all local — no react-query on order surfaces).
  const [accounts, setAccounts] = useState([]);
  const [accountsLoaded, setAccountsLoaded] = useState(false);
  const [accountsFailed, setAccountsFailed] = useState(false);
  const [shipments, setShipments] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState('');
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({
    service_code: '',
    weight: '',
    length: '',
    width: '',
    height: '',
    reference: '',
  });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [quote, setQuote] = useState({ status: 'idle', data: null });
  // What the carrier will receive (POST /api/orders/{id}/shipping-info) —
  // shown before creating; creation is blocked until it loads cleanly.
  const [shippingInfo, setShippingInfo] = useState({ status: 'idle', data: null });
  // Confirm-step corrections, per block and per field. Sent with every
  // shipping-info preview so missing[]/ready always describe the EDITED
  // values, and again on create so the server re-validates the same input.
  const [overrides, setOverrides] = useState({ ship_to: {}, ship_from: {} });
  // Opt-in: also persist the corrected ship-to as the order's shipping address.
  const [saveToRecords, setSaveToRecords] = useState(false);
  // Missing-details list from a 422 on POST (race: data changed after the
  // pre-check) — takes display precedence until shipping-info re-syncs.
  const [missing422, setMissing422] = useState([]);
  const [creatingShipment, setCreatingShipment] = useState(false);
  const [savingManual, setSavingManual] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [warning, setWarning] = useState('');
  const [apiSuccess, setApiSuccess] = useState('');
  const fetchedRef = useRef(false);

  const opened = lastStep >= STEPS.TRACKING_NUMBER;

  const sortShipments = (list) =>
    [...list].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));

  // Stable identity so the card's auto-poll effect doesn't reset every render.
  // `poll` ({ failing }) is passed only by the card's 5 s auto-poll (usePoll):
  // repeats of a failure go quiet and the error is rethrown so the poll backs
  // off / stops. Every other caller passes nothing and keeps the old swallow.
  const refetchShipments = useCallback(async (poll) => {
    if (!order?.id) return;
    try {
      const res = requireResponse(await axios.get(
        `/api/orders/${order.id}/shipments`,
        poll ? { metadata: { quiet: poll.failing } } : undefined
      ));
      const { data } = res;
      setShipments(sortShipments(Array.isArray(data) ? data : []));
    } catch (e) {
      // Non-blocking — the legacy path still works without shipment history.
      if (poll) throw e;
    }
  }, [order?.id]);

  // Destination params for the services + quote endpoints, when the order
  // exposes a shipping address (omitted otherwise).
  const addressParams = () => {
    const params = {};
    const addr = order?.shipping_address;
    const country =
      addr?.country?.alpha2Code ||
      addr?.country?.code ||
      (typeof addr?.country === 'string' ? addr.country : undefined);
    const postcode = addr?.postal_code || addr?.postcode;
    if (country) params.country = country;
    if (postcode) params.postcode = postcode;
    return params;
  };

  // Fetch enabled accounts + existing shipments once, when the step opens.
  useEffect(() => {
    if (!opened || !order?.id || fetchedRef.current) return;
    fetchedRef.current = true;
    (async () => {
      try {
        const params = order.store_id_m ? { store_id_m: order.store_id_m } : {};
        const { data } = await axios.get('/api/shipping-accounts', { params });
        const enabled = (Array.isArray(data) ? data : []).filter(
          (a) => a.is_enabled !== false
        );
        setAccounts(enabled);
        // An order that already has a legacy tracking number opens straight
        // on the manual input, prefilled — same experience as before.
        if (order.tracking_number) setSelectedPartner(MANUAL_OPTION);
      } catch (e) {
        setAccountsFailed(true);
      } finally {
        setAccountsLoaded(true);
      }
      await refetchShipments();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, order?.id]);

  const selectedAccount = accounts.find((a) => String(a.id) === String(selectedPartner));
  const isApiPartner = !!selectedAccount?.supports_api;
  const isManualAccount = !!selectedAccount && !selectedAccount.supports_api;
  const isLegacyPartner = selectedPartner === MANUAL_OPTION;

  // Tracks the currently-selected account so out-of-order shipping-info / quote
  // responses (partner A→B switch) are discarded instead of showing A's data
  // under B — which could let a rep confirm a billable shipment against the
  // wrong ship-to/ship-from.
  const selectedAccountIdRef = useRef(null);
  useEffect(() => {
    selectedAccountIdRef.current = selectedAccount?.id ?? null;
  }, [selectedAccount?.id]);

  // Monotonic preview counter: a slow earlier shipping-info response must
  // never overwrite a newer one, or a stale ready=true could let a rep confirm
  // a billable shipment against data the server has since rejected.
  const previewSeqRef = useRef(0);

  // Fetched per selected partner: ship_from comes from the ACCOUNT's
  // collection address (e.g. DHL UK vs DHL France), store details fallback.
  // POSTed with the confirm-step edits so missing[]/ready describe exactly
  // what create would decide. Read-only on the server — creates nothing.
  const fetchShippingInfo = async (accountId, pendingOverrides) => {
    if (!order?.id) return;
    const seq = ++previewSeqRef.current;
    // Keep the previous answer visible while re-validating an edit (only the
    // first load blanks the block); Confirm stays disabled either way because
    // neither status is 'done'.
    setShippingInfo((prev) =>
      prev.data
        ? { status: 'revalidating', data: prev.data }
        : { status: 'loading', data: null }
    );
    try {
      const { data } = await axios.post(`/api/orders/${order.id}/shipping-info`, {
        ...(accountId ? { shipping_account_id: accountId } : {}),
        ship_to_override: pendingOverrides?.ship_to || {},
        ship_from_override: pendingOverrides?.ship_from || {},
      });
      // Discard if the partner changed, or a newer preview already answered.
      if (accountId && selectedAccountIdRef.current !== accountId) return;
      if (seq !== previewSeqRef.current) return;
      setShippingInfo({ status: 'done', data });
      // Fresh server state supersedes any stale 422 list.
      setMissing422([]);
    } catch (e) {
      if (accountId && selectedAccountIdRef.current !== accountId) return;
      if (seq !== previewSeqRef.current) return;
      // Fail-closed: a rejected preview must not leave stale ready=true data
      // on screen for the rep to confirm against.
      setShippingInfo({ status: 'failed', data: null });
    }
  };

  const setOverrideField = (block, field, value) => {
    setOverrides((prev) => ({ ...prev, [block]: { ...prev[block], [field]: value } }));
  };

  const resetOverrideBlock = (block) => {
    setOverrides((prev) => ({ ...prev, [block]: {} }));
  };

  const hasOverrides =
    Object.keys(overrides.ship_to).length > 0 ||
    Object.keys(overrides.ship_from).length > 0;

  // Cost quote for the confirm modal. Read-only and free; a slow or failed
  // quote never blocks confirming — only the cost line changes.
  const quoteSeqRef = useRef(0);

  const fetchQuote = async () => {
    if (!selectedAccount) return;
    const accountId = selectedAccount.id;
    const seq = ++quoteSeqRef.current;
    setQuote({ status: 'loading', data: null });
    try {
      // Quote the destination actually being booked: the server's merged
      // ship-to (so a corrected country/postcode is priced), falling back to
      // the order's raw shipping address before the preview has landed.
      const shipTo = shippingInfo.data?.ship_to;
      const params =
        shipTo?.country || shipTo?.postcode
          ? {
              ...(shipTo.country ? { country: shipTo.country } : {}),
              ...(shipTo.postcode ? { postcode: shipTo.postcode } : {}),
            }
          : addressParams();
      if (form.service_code) params.service_code = form.service_code;
      if (form.weight) params.weight_kg = form.weight;
      if (form.length) params.length_cm = form.length;
      if (form.width) params.width_cm = form.width;
      if (form.height) params.height_cm = form.height;
      const { data } = await axios.get(
        `/api/shipping-accounts/${accountId}/quote`,
        { params }
      );
      // Discard if the partner changed, or a newer quote already answered.
      if (selectedAccountIdRef.current !== accountId) return;
      if (seq !== quoteSeqRef.current) return;
      setQuote({ status: 'done', data });
    } catch (e) {
      if (selectedAccountIdRef.current !== accountId) return;
      if (seq !== quoteSeqRef.current) return;
      setQuote({ status: 'failed', data: null });
    }
  };

  // Load available services + the pass-through shipping details whenever the
  // selected API account changes; seed the reference.
  useEffect(() => {
    if (!selectedAccount?.supports_api) return;
    let cancelled = false;
    setServices([]);
    setMissing422([]);
    setForm((f) => ({
      ...f,
      service_code: '',
      reference: f.reference || String(order?.id || ''),
    }));
    // A different partner means a different ship-from and possibly a different
    // country: corrections made against the previous one must not carry over.
    setOverrides({ ship_to: {}, ship_from: {} });
    setSaveToRecords(false);
    // The preview itself is owned by the effect below (it also re-runs on edit).
    (async () => {
      try {
        const { data } = await axios.get(
          `/api/shipping-accounts/${selectedAccount.id}/services`,
          { params: addressParams() }
        );
        if (!cancelled) setServices(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setServices([]);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount?.id]);

  // Single owner of the shipping-info preview: re-runs on partner change and
  // on every correction. Debounced while editing so a typed postcode is one
  // request, not eight; immediate on partner change so the block isn't blank
  // for no reason.
  const overridesKey = JSON.stringify(overrides);
  useEffect(() => {
    if (!selectedAccount?.supports_api) return;
    const accountId = selectedAccount.id;
    const delay = hasOverrides ? 400 : 0;
    const timer = setTimeout(() => fetchShippingInfo(accountId, overrides), delay);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount?.id, selectedAccount?.supports_api, overridesKey]);

  // Re-quote while the modal is open whenever a parcel detail or the (possibly
  // corrected) destination changes, so the cost line describes the parcel
  // actually being booked. Debounced — typing a weight is one request.
  const quoteKey = [
    form.service_code,
    form.weight,
    form.length,
    form.width,
    form.height,
    shippingInfo.data?.ship_to?.country,
    shippingInfo.data?.ship_to?.postcode,
  ].join('|');
  useEffect(() => {
    if (!confirmOpen || !selectedAccount?.supports_api) return;
    const timer = setTimeout(() => fetchQuote(), 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmOpen, selectedAccount?.id, quoteKey]);

  if (lastStep < STEPS.TRACKING_NUMBER) {
    return null;
  }

  const activeShipment = shipments.find((s) => ACTIVE_STATUSES.includes(s.status));
  const errorShipment = !activeShipment
    ? shipments.find((s) => s.status === 'error')
    : null;
  const displayShipment = activeShipment || errorShipment;

  const showPartnerPicker = accountsLoaded && !accountsFailed && accounts.length > 0;
  const legacyMode = !showPartnerPicker || isLegacyPartner;

  const partnerOptions = [
    ...accounts.map((a) => ({
      id: String(a.id),
      name: a.environment === 'uat' ? `${a.label} (Sandbox)` : a.label,
    })),
    { id: MANUAL_OPTION, name: 'Other / enter manually' },
  ];

  const celebrationMessage = () => {
    if (!isPxOrder && !paidAmount) {
      return 'Cart order successfully created to Client’s basket.';
    }
    if (!isPxOrder && paidAmount > 0) {
      return 'Cart order successfully created to Client with payment update.';
    }
    if (isPxOrder && !paidAmount) {
      return 'PX Cart order successfully created to Client order page.';
    }
    return 'PX Cart order successfully created to Client with payment update.';
  };

  // Legacy path — unchanged: PUT /api/orders/{id} via the constate store.
  const handleSave = async () => {
    try {
      await saveTrackingNumber(trackingNumber);
      setSuccessMessage(celebrationMessage());
    } catch (error) {
      console.error('Error saving tracking number', error);
    }
  };

  const handle409 = (err) => {
    setWarning(
      err?.response?.data?.message || 'A shipment already exists for this order.'
    );
    refetchShipments();
  };

  // Manual carrier account — the shipment row builds the tracking link from
  // the account's URL template.
  const handleManualAccountSave = async () => {
    if (savingManual || !order?.id) return;
    if (!trackingNumber) {
      setWarning('Enter a tracking number first.');
      return;
    }
    setWarning('');
    setSavingManual(true);
    try {
      const { data } = await axios.post(`/api/orders/${order.id}/shipments`, {
        shipping_account_id: selectedAccount.id,
        tracking_number: trackingNumber,
      });
      if (data) setShipments((prev) => sortShipments([data, ...prev]));
      setSuccessMessage(celebrationMessage());
    } catch (err) {
      if (err?.response?.status === 409) {
        handle409(err);
      } else {
        setWarning(err?.response?.data?.message || 'Failed to save the tracking number.');
      }
    } finally {
      setSavingManual(false);
    }
  };

  const openConfirm = () => {
    setConfirmOpen(true);
    // The quote itself is fired by the effect near fetchQuote, which also
    // re-runs when a parcel detail or the destination is corrected in the modal.
    setQuote({ status: 'loading', data: null });
  };

  const quoteLine = () => {
    if (quote.status === 'loading') {
      return <p className="shipment-confirm__cost">Fetching cost estimate…</p>;
    }
    if (quote.status === 'done' && quote.data) {
      const { amount, currency, source, message } = quote.data;
      if (source === 'carrier' && amount != null) {
        const iso = (currency || getBaseCurrency()).toUpperCase();
        const major = (amount / 100).toFixed(2);
        // Shared symbol table; unknown currency → prefix the ISO code
        // (AED 123.45) rather than mislabelling it as £.
        const symbol = currencyMeta(iso)?.symbol;
        const display = symbol ? `${symbol}${major}` : `${iso} ${major}`;
        return (
          <p className="shipment-confirm__cost">
            Estimated cost: <strong>{display}</strong>
          </p>
        );
      }
      return (
        <p className="shipment-confirm__cost">
          Cost: billed by {selectedAccount?.carrier_name || selectedAccount?.carrier}{' '}
          at your account rates{message ? ` — ${message}` : ''}
        </p>
      );
    }
    if (quote.status === 'failed') {
      return <p className="shipment-confirm__cost">Cost estimate unavailable.</p>;
    }
    return null;
  };

  // API carrier account — POST after explicit confirmation (real shipment,
  // carrier charges may apply).
  const handleCreateShipment = async () => {
    if (creatingShipment || !order?.id || !selectedAccount) return;
    setWarning('');
    setCreatingShipment(true);
    try {
      const payload = { shipping_account_id: selectedAccount.id };
      if (form.service_code) payload.service_code = form.service_code;
      if (form.weight) payload.weight_kg = Number(form.weight);
      if (form.length) payload.length_cm = Number(form.length);
      if (form.width) payload.width_cm = Number(form.width);
      if (form.height) payload.height_cm = Number(form.height);
      if (form.reference) payload.reference = form.reference;
      // The same corrections the preview validated. The server re-merges and
      // re-checks them, so this cannot skip the mandatory-field gate.
      if (Object.keys(overrides.ship_to).length > 0) {
        payload.ship_to_override = overrides.ship_to;
      }
      if (Object.keys(overrides.ship_from).length > 0) {
        payload.ship_from_override = overrides.ship_from;
      }
      if (hasOverrides && saveToRecords) payload.save_to_records = true;

      const { data } = await axios.post(`/api/orders/${order.id}/shipments`, payload);
      setConfirmOpen(false);
      if (data) setShipments((prev) => sortShipments([data, ...prev]));
      if (data?.tracking_number) setTrackingNumber(data.tracking_number);
      setApiSuccess(`Shipment created with ${selectedAccount.label}.`);
    } catch (err) {
      if (err?.response?.status === 409) {
        setConfirmOpen(false);
        handle409(err);
      } else if (err?.response?.status === 422) {
        const missing = err.response.data?.missing;
        if (Array.isArray(missing) && missing.length > 0) {
          // Race: mandatory details changed since the pre-check. Show the
          // server's list and re-sync the pass-through summary. The modal STAYS
          // open — the details are editable right here, so closing it would
          // just make the rep reopen it to fix what we are complaining about.
          setMissing422(missing);
          setWarning(
            err.response.data?.message || 'Mandatory shipping details are missing.'
          );
          fetchShippingInfo(selectedAccount.id, overrides);
        } else {
          setConfirmOpen(false);
          const errors = err.response.data?.errors;
          const first = errors && Object.values(errors).flat()[0];
          setWarning(
            first || err.response.data?.message || 'Validation failed — check the shipment details.'
          );
        }
      } else {
        setConfirmOpen(false);
        setWarning(err?.response?.data?.message || 'Failed to create the shipment — try again.');
      }
    } finally {
      setCreatingShipment(false);
    }
  };

  const replaceShipment = (updated) => {
    if (!updated?.id) return;
    setShipments((prev) => sortShipments(prev.map((s) => (s.id === updated.id ? updated : s))));
    if (updated.tracking_number) setTrackingNumber(updated.tracking_number);
  };

  // Manual shipments only — PUT the corrected number onto the SAME row.
  const handleUpdateTracking = async (shipment, newTrackingNumber) => {
    const { data } = await axios.put(`/api/shipments/${shipment.id}/tracking`, {
      tracking_number: newTrackingNumber,
    });
    replaceShipment(data);
  };

  const handleRetry = async (shipment) => {
    if (actionBusy) return;
    setWarning('');
    setActionBusy(true);
    try {
      const { data } = await axios.post(`/api/shipments/${shipment.id}/retry`);
      replaceShipment(data);
    } catch (err) {
      setWarning(err?.response?.data?.message || 'Retry failed — try again.');
    } finally {
      setActionBusy(false);
    }
  };

  const handleRefreshTracking = async (shipment) => {
    if (actionBusy) return;
    setWarning('');
    setActionBusy(true);
    try {
      const { data } = await axios.post(`/api/shipments/${shipment.id}/refresh-tracking`);
      replaceShipment(data);
    } catch (err) {
      setWarning(err?.response?.data?.message || 'Failed to refresh tracking.');
    } finally {
      setActionBusy(false);
    }
  };

  const manualInputAndButtons = (onSave, saving) => (
    <div>
      <div className="row">
        <div className="col-12">
          <FieldGroup
            type="text"
            name="tracking_number"
            persistLabel
            inputStyle={{ paddingLeft: '170px' }}
            placeholder="Tracking Number:"
            noFormik
            value={trackingNumber}
            onChange={(e) => setTrackingNumber(e.target.value)}
          />
        </div>
      </div>

      <div className="action-buttons text-right mt-5">
        <Button variant="secondary" data-btn="tracking-open-action" onClick={() => setShowDialog(true)}>
          Run action
        </Button>
        <Button variant="primary" data-btn="tracking-save" loading={saving} onClick={onSave}>
          Save & close
        </Button>
      </div>
    </div>
  );

  // Missing-mandatory-details list: a fresh 422 response wins until the
  // shipping-info refetch supersedes it.
  const missingDisplay =
    missing422.length > 0 ? missing422 : shippingInfo.data?.missing || [];

  // Part-exchange orders: the parcel travels client→store (wrong direction for
  // outbound API booking), so the backend blocks it. Record tracking manually.
  const isPxShipment = shippingInfo.status === 'done' && !!shippingInfo.data?.is_px;

  // Opening the review step creates nothing and charges nothing, so missing
  // details deliberately do NOT block it — the review step is where they get
  // corrected. A failed shipping-info still does: with no server answer we
  // cannot show what would be sent, and editing from a blank block would be
  // guesswork.
  const reviewDisabled =
    creatingShipment ||
    shippingInfo.status === 'idle' ||
    shippingInfo.status === 'loading' ||
    shippingInfo.status === 'failed' ||
    isPxShipment;

  // Fail-safe: never BOOK from unverified data — Confirm stays disabled until
  // shipping-info has answered cleanly, for the current edits, with nothing
  // missing ('revalidating' is not 'done', so an in-flight edit blocks too).
  const createDisabled =
    creatingShipment ||
    shippingInfo.status !== 'done' ||
    missingDisplay.length > 0 ||
    isPxShipment ||
    shippingInfo.data?.ready === false;

  const selectedServiceName = services.find((s) => s.code === form.service_code)?.name;

  const missingList = missingDisplay.length > 0 && (
    <div className="shipment-missing">
      <div className="shipment-card__note shipment-card__note--error">
        Missing mandatory shipping details:
      </div>
      <ul className="shipment-missing__list">
        {missingDisplay.map((m) => (
          <li key={`${m.field}-${m.where}`}>
            {m.label}
            {m.where ? ` (${m.where})` : ''}
          </li>
        ))}
      </ul>
      <div className="shipment-missing__hint">
        Fill them in on the client / order, or correct them for this shipment in
        the review step (REVIEW SHIPMENT &amp; LABEL → Edit).
      </div>
    </div>
  );

  const apiShipmentForm = (
    <div className="shipment-form">
      {shippingInfo.status === 'loading' && (
        <p className="shipment-ship-to__loading">Loading client shipping details…</p>
      )}
      {shippingInfo.status === 'failed' && (
        <div className="shipment-card__note shipment-card__note--warn">
          Could not load client shipping details — shipment creation is
          disabled until they load.
        </div>
      )}
      {(shippingInfo.status === 'done' || shippingInfo.status === 'revalidating') && (
        <div className="shipment-ship-to">
          <AddressBlock
            title="Ship to (assigned client)"
            addr={shippingInfo.data?.ship_to}
          />
          <p className="shipment-ship-to__note">
            Taken from the assigned client and the order's shipping address. You
            can review everything — and correct it for this shipment — before
            anything is sent to the carrier.
            {hasOverrides && (
              <strong> Edited for this shipment; review before confirming.</strong>
            )}
          </p>
        </div>
      )}
      {isPxShipment && (
        <div className="shipment-card__note shipment-card__note--warn">
          Automatic shipment booking isn't available for part-exchange orders —
          record a manual tracking number instead.
        </div>
      )}
      {missingList}
      {services.length > 0 && (
        <div className="row">
          <div className="col-12">
            <FieldGroup
              type="select"
              name="shipping_service"
              noFormik
              placeholder="Select service"
              options={services.map((s) => ({ id: s.code, name: s.name }))}
              value={form.service_code}
              onChange={(code) => setForm((f) => ({ ...f, service_code: code }))}
            />
          </div>
        </div>
      )}
      <div className="row shipment-form__dims">
        <div className="col-3">
          <FieldGroup
            type="number"
            name="shipment_weight"
            noFormik
            min={0}
            step="0.1"
            placeholder="Weight (kg)"
            value={form.weight}
            onChange={(e) => setForm((f) => ({ ...f, weight: e.target.value }))}
          />
        </div>
        <div className="col-3">
          <FieldGroup
            type="number"
            name="shipment_length"
            noFormik
            min={0}
            step="1"
            placeholder="Length (cm)"
            value={form.length}
            onChange={(e) => setForm((f) => ({ ...f, length: e.target.value }))}
          />
        </div>
        <div className="col-3">
          <FieldGroup
            type="number"
            name="shipment_width"
            noFormik
            min={0}
            step="1"
            placeholder="Width (cm)"
            value={form.width}
            onChange={(e) => setForm((f) => ({ ...f, width: e.target.value }))}
          />
        </div>
        <div className="col-3">
          <FieldGroup
            type="number"
            name="shipment_height"
            noFormik
            min={0}
            step="1"
            placeholder="Height (cm)"
            value={form.height}
            onChange={(e) => setForm((f) => ({ ...f, height: e.target.value }))}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <FieldGroup
            type="text"
            name="shipment_reference"
            noFormik
            placeholder="Reference"
            value={form.reference}
            onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))}
          />
        </div>
      </div>
      <div className="action-buttons text-right mt-5">
        <Button
          variant="primary"
          data-btn="tracking-open-review"
          disabled={reviewDisabled}
          title={
            isPxShipment
              ? "Not available for part-exchange orders — record a manual tracking number."
              : missingDisplay.length > 0
              ? 'Opens the review step, where you can fill these in for this shipment.'
              : 'Review everything before anything is sent to the carrier.'
          }
          onClick={openConfirm}
        >
          Review shipment &amp; label
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <Collapsible
        trigger={<SectionTrigger icon="tracking" label="Tracking Number" />}
        open={step === STEPS.TRACKING_NUMBER}
        handleTriggerClick={
          isInline
            ? () =>
                setStep(
                  step === STEPS.TRACKING_NUMBER ? -1 : STEPS.TRACKING_NUMBER
                )
            : undefined
        }
      >
        <div className="tracking-number mt-4">
          {successMessage && <Alert type="success" message={successMessage} />}
          {!successMessage && apiSuccess && <Alert type="success" message={apiSuccess} />}
          {warning && <Alert type="warning" message={warning} />}

          {displayShipment ? (
            <ShipmentCard
              shipment={displayShipment}
              busy={actionBusy}
              onRetry={handleRetry}
              onRefresh={handleRefreshTracking}
              onCheckStatus={refetchShipments}
              onUpdateTracking={handleUpdateTracking}
            />
          ) : successMessage ? null : !accountsLoaded ? (
            // Don't flash the legacy input while accounts load — a rep could
            // start typing/saving into a path that's about to be replaced.
            <Loading type="small" />
          ) : (
            <div>
              {showPartnerPicker && (
                <div className="row">
                  <div className="col-12">
                    <FieldGroup
                      type="select"
                      name="shipping_partner"
                      noFormik
                      placeholder="Select shipping partner"
                      options={partnerOptions}
                      value={selectedPartner}
                      onChange={(id) => {
                        setSelectedPartner(id);
                        setWarning('');
                      }}
                    />
                  </div>
                </div>
              )}

              {isApiPartner && apiShipmentForm}

              {isManualAccount && manualInputAndButtons(handleManualAccountSave, savingManual)}

              {legacyMode && manualInputAndButtons(handleSave, false)}
            </div>
          )}
        </div>
      </Collapsible>

      <ReactModal
        {...{
          isOpen: showDialog,
          onRequestClose: () => setShowDialog(false),
          portalClassName: 'payment-dialog-portal',
          style: modalStyle,
        }}
      >
        <div className="tracking-dialog">
          <Alert
            type="success"
            message={'You rock!'}
          >
          </Alert>
          <div className="action-buttons text-right mt-4">
            <Button variant="primary" data-btn="tracking-confirm-notice" onClick={() => setShowDialog(false)}>
              I know!
            </Button>
          </div>
        </div>
      </ReactModal>

      <ReactModal
        {...{
          isOpen: confirmOpen,
          onRequestClose: () => !creatingShipment && setConfirmOpen(false),
          portalClassName: 'payment-dialog-portal',
          style: modalStyle,
        }}
      >
        <div className="tracking-dialog shipment-confirm">
          <h4>Review shipment</h4>
          <p>
            Check everything below — edit anything that is wrong — then confirm.
            Confirming creates a real shipment with{' '}
            <strong>{selectedAccount?.label}</strong>{' '}
            <EnvBadge environment={selectedAccount?.environment} />. Carrier
            charges may apply. Nothing is sent until you confirm.
          </p>

          {/* Full pass-through review — exactly what will be sent, and
              editable in place. Values always come from the server's merged
              shipping-info, so the review can't drift from the payload. */}
          <div className="shipment-confirm__review">
            <div className="shipment-confirm__addresses">
              <EditableAddressBlock
                title="Ship to"
                addr={shippingInfo.data?.ship_to}
                defaults={shippingInfo.data?.defaults?.ship_to}
                edits={overrides.ship_to}
                disabled={creatingShipment}
                onChange={(field, value) => setOverrideField('ship_to', field, value)}
                onReset={() => resetOverrideBlock('ship_to')}
              />
              <EditableAddressBlock
                title="Ship from"
                addr={shippingInfo.data?.ship_from}
                defaults={shippingInfo.data?.defaults?.ship_from}
                edits={overrides.ship_from}
                disabled={creatingShipment}
                onChange={(field, value) => setOverrideField('ship_from', field, value)}
                onReset={() => resetOverrideBlock('ship_from')}
              />
            </div>

            {/* Parcel details, editable here too — same state as the form
                behind the modal, so the two never disagree. */}
            <div className="shipment-confirm__parcel">
              <label>
                <span>Service</span>
                {services.length > 0 ? (
                  <select
                    value={form.service_code}
                    disabled={creatingShipment}
                    onChange={(e) => setForm((f) => ({ ...f, service_code: e.target.value }))}
                  >
                    <option value="">Carrier default</option>
                    {services.map((s) => (
                      <option key={s.code} value={s.code}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="shipment-confirm__static">
                    {selectedServiceName || 'Carrier default'}
                  </span>
                )}
              </label>
              <div className="shipment-confirm__dims">
                {[
                  { key: 'weight', label: 'Weight (kg)', step: '0.1' },
                  { key: 'length', label: 'Length (cm)', step: '1' },
                  { key: 'width', label: 'Width (cm)', step: '1' },
                  { key: 'height', label: 'Height (cm)', step: '1' },
                ].map((f) => (
                  <label key={f.key}>
                    <span>{f.label}</span>
                    <input
                      type="number"
                      min={0}
                      step={f.step}
                      value={form[f.key]}
                      disabled={creatingShipment}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, [f.key]: e.target.value }))
                      }
                    />
                  </label>
                ))}
              </div>
              <label>
                <span>Reference</span>
                <input
                  type="text"
                  autoComplete="off"
                  value={form.reference}
                  disabled={creatingShipment}
                  onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))}
                />
              </label>
            </div>
          </div>

          {/* Same authoritative list as the form behind the modal — an edit
              that clears a mandatory field lands here and blocks Confirm. */}
          {missingList}

          {hasOverrides && (
            <label className="shipment-confirm__save-back">
              <input
                type="checkbox"
                checked={saveToRecords}
                disabled={creatingShipment}
                onChange={(e) => setSaveToRecords(e.target.checked)}
              />
              <span>
                Also save this address to the order
                <em>
                  Otherwise the change applies to this shipment only — the
                  order’s shipping address stays as it is. The client record is
                  never changed either way.
                </em>
              </span>
            </label>
          )}

          {quoteLine()}
          <ButtonGroup align="end" className="action-buttons mt-4">
            <Button
              variant="secondary"
              data-btn="tracking-cancel-create"
              disabled={creatingShipment}
              onClick={() => setConfirmOpen(false)}
            >
              Cancel
            </Button>
            {/* Label swap kept (not `loading`): two distinct busy states,
                "Creating…" and "Checking…", must stay distinguishable. */}
            <Button
              variant="primary"
              data-btn="tracking-create"
              disabled={createDisabled}
              title={
                shippingInfo.status === 'revalidating'
                  ? 'Checking the edited details…'
                  : missingDisplay.length > 0
                  ? 'Complete the missing details above first.'
                  : undefined
              }
              onClick={handleCreateShipment}
            >
              {creatingShipment
                ? 'Creating…'
                : shippingInfo.status === 'revalidating'
                ? 'Checking…'
                : 'Confirm — create shipment'}
            </Button>
          </ButtonGroup>
        </div>
      </ReactModal>
    </>
  );
}
